import { Badge, Button, CreateTableState, Grid, Icon, Typography } from 'gu-ui-kit'
import React, { useMemo } from 'react'
import styles from 'modules/Finance/PaymentProfiles/subcomponents/List/List.module.scss'
import { Link } from 'react-router-dom'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useTranslation } from 'react-i18next'
import { createPath } from 'history'
import qs from 'qs'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

export const useTableConfig = ({ className, ...rest }, data = []) => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  const tableConfig = {
    className,
    state: {
      columnVisibility: {},
    },
    columns: [
      {
        header: t('payment_profiles::data_list.headers.name'),
        id: 'name',
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: { is_archived, status, id, title, contract, method, decline_reason },
          },
        }) => {
          const statusBadgeColors = useMemo(() => {
            switch (status) {
              case 'new':
                return { bgColor: 'Background-Fifth', color: 'Label-GU-Primary' }
              case 'approved':
                return { bgColor: 'Background-Confirm', color: 'Label-Confirm' }
              case 'declined':
                return { bgColor: 'Background-Error', color: 'Label-Error-Secondary' }
              case 'pending':
                return { bgColor: 'Background-Notice', color: 'Label-Notice' }
              default:
                return { bgColor: 'Background-Tertiary' }
            }
          }, [status])

          return (
            <Grid
              gap={16}
              wrap={false}
            >
              <div>
                <div className={styles['icon__wrapper']}>
                  <Icon
                    color={'Interface-Eighth'}
                    name={'user'}
                    size={'m'}
                  />
                </div>
              </div>
              <Grid
                gap={4}
                direction={'column'}
                className={styles['word-break-container']}
              >
                <Typography
                  noMargin
                  color={'Label-Fifth'}
                  variant={'Body-Forms-15'}
                  component={
                    status !== 'new' ? (
                      <Link
                        to={createPath({
                          pathname: pathGen(ROUTES.FINANCE_INVOICES),
                          search: qs.stringify(
                            {
                              payment_profile_ids: [id],
                            },
                            {
                              arrayFormat: 'brackets',
                            },
                          ),
                        })}
                      >
                        {title}
                      </Link>
                    ) : (
                      <span>{title}</span>
                    )
                  }
                />

                <Grid gap={8}>
                  {contract?.number && (
                    <>
                      <Badge
                        variant='Body-10'
                        rounded
                        size={'xs'}
                        bgColor={'Background-Fifth'}
                        color={'Label-GU-Primary'}
                      >
                        {contract.number}
                      </Badge>
                    </>
                  )}

                  <Badge
                    variant='Body-10'
                    size={'xs'}
                    rounded
                    bgColor={'Background-Fifth'}
                  >
                    {t(`common::payment_methods.${method}`)}
                  </Badge>

                  <Badge
                    variant='Body-10'
                    size={'xs'}
                    rounded
                    {...statusBadgeColors}
                  >
                    {is_archived
                      ? t(`payment_profiles::is_archived`)
                      : t(`payment_profiles::statuses.${status}`)}

                    {status === 'declined' && decline_reason && (
                      <span>
                        : <span className={styles['decline_reason']}>{decline_reason}</span>
                      </span>
                    )}
                  </Badge>
                </Grid>
              </Grid>
            </Grid>
          )
        },
      },
      {
        id: 'action',
        cell: ({
          row: {
            original: { status, id },
          },
        }) => (
          <Grid justifyContent={'flex-end'}>
            {status === 'declined' ? (
              <Button
                variant={'ghost'}
                size={'s'}
                component={Link}
                to={pathGen(ROUTES.FINANCE_PAYMENT_PROFILE_EDIT, {
                  profileId: id,
                })}
                title={t('common::buttons.edit')}
                startIcon={
                  <Icon
                    name={'edit'}
                    size={'s'}
                  />
                }
                noLabel={!breakpoint.sm}
              >
                {t('common::buttons.edit')}
              </Button>
            ) : (
              <Button
                component={Link}
                to={createPath({
                  pathname: pathGen(ROUTES.FINANCE_INVOICES),
                  search: qs.stringify(
                    {
                      payment_profile_ids: [id],
                    },
                    {
                      arrayFormat: 'brackets',
                    },
                  ),
                })}
                variant={'ghost'}
                size={'s'}
                color={'Label-GU-Primary'}
                startIcon={<Icon name={'document'} />}
                noLabel={!breakpoint.sm}
              >
                {t('common::buttons.go_to_invoices')}
              </Button>
            )}
          </Grid>
        ),
      },
    ],
    showHeader: data?.length > 0,
    showFooter: false,
    ...rest,
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
